import { animate, group, state, style, transition, trigger } from '@angular/animations';
import { Component, AfterViewInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { GridApi, GridOptions, IDatasource, IGetRowsParams, RowGroupingDisplayType, StatusPanelDef, ColDef, ICellRendererParams } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, retry } from 'rxjs';
import { AddressTooltipComponent } from 'src/app/cell-renderer/address-tooltip/address-tooltip.component';
import { BtnActionComponent } from 'src/app/cell-renderer/btn-action/btn-action.component';
import { GroupCellComponent } from 'src/app/cell-renderer/group-cell/group-cell.component';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { ModalEvidenceComponent } from 'src/app/cell-renderer/modal-evidence/modal-evidence.component';
import { StatusSelectComponent } from 'src/app/cell-renderer/status-select/status-select.component';
import { DetailPanelComponent } from 'src/app/cell-renderer/detail-panel/detail-panel.component';
import { ClientService } from 'src/app/services/client.service';
import { OrderService } from 'src/app/services/order.service';
import { ExpenseService } from 'src/app/services/expense.service';
import { ServiceNotesService } from 'src/app/services/service-notes.service';
import { UtilsService } from 'src/app/services/utils.service';
import { environment } from 'src/environments/environment';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

@Component({
    selector: 'app-order-list',
    templateUrl: './order-list.component.html',
    styleUrls: ['./order-list.component.css'],
    animations: [
        trigger('fade', [
            state('visible', style({ opacity: 1 })),
            state('hidden', style({ opacity: 0 })),
            transition('visible <=> hidden', animate('.3s ease-in-out')),
        ]),
    ]
})
export class OrderListComponent implements AfterViewInit {
    constructor(
        private apiService: OrderService,
        private expenseService: ExpenseService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService,
        private apiClient: ClientService,
        private router: Router,
        private apiServiceNote: ServiceNotesService,
        private permissionService: PermissionService,
        private route: ActivatedRoute,
    ) { }

    private formatDateTime(input: any): string {
        try {
            const d = new Date(input);
            if (isNaN(d.getTime())) return String(input ?? '');
            return d.toLocaleString('es-CO', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: false
            });
        } catch {
            return String(input ?? '');
        }
    }

    refreshGrid() {
        this.buildDatasource();
        this.loadSharedList();
    }

    private buildDisplayRows(): any[] {
        const out: any[] = [];
        for (const r of this.baseRows) {
            out.push(r);
            if (r && this.expandedIds.has(r.id)) {
                out.push({ __isDetail: true, master: r });
            }
        }
        return out;
    }

    private buildSharedDisplayRows(): any[] {
        const out: any[] = [];
        for (const group of this.sharedGroups) {
            const firstService = group.services?.[0];
            const unitName: string = (group as any).unit ?? (group as any).unit_name ?? firstService?.unified_concat ?? '';
            const operatorName: string = (group as any).driver_name ?? firstService?.operator_name ?? '';
            out.push({ __isGroupHeader: true, group_id: group.group_id, group_code: group.group_code, services_count: group.services?.length ?? 0, unit_name: unitName, operator_name: operatorName, active_services_count: group.active_services_count ?? 0, first_active_service_id: group.first_active_service_id ?? null });
            if (this.expandedGroups.has(group.group_id)) {
                for (const s of (group.services || [])) {
                    out.push(s);
                    const sid = s.id;
                    if (this.expandedIds.has(sid)) out.push({ __isDetail: true, master: s });
                }
            }
        }
        return out;
    }

    /**
     * Helper: determines if a given status indicates finalization.
     */
    private isFinalized(statusText?: any, statusId?: any): boolean {
        try {
            const txt = String(statusText ?? '').toLowerCase();
            if (txt.includes('finaliz')) return true;
        } catch { }
        try {
            const idNum = Number(statusId);
            if ([5, 6].includes(idNum)) return true; // finalized statuses
        } catch { }
        return false;
    }

    /**
     * Resolve color for Estado column matching the same business rules used for text:
     * - If service is finalized and there is an address_service pending, use that address's color.
     * - Otherwise, use service color.
     */
    private getStatusColor(row: any): { color: string; bg: string } {
        if (!row) return { color: '', bg: '' };
        const serviceColor = row?.status_color || '';
        const serviceBg = row?.bgColor || '';
        const addr = Array.isArray(row?.address_services) ? row.address_services : [];
        const serviceStatusId = Number(row?.status_id);
        const serviceIsFinal = this.isFinalized(row?.status, row?.status_id);
        
        let finalColor = serviceColor;
        let finalBg = serviceBg;
        
        if (addr.length > 0) {
            if (serviceStatusId === 5) {
                // Use first address whose status is NOT 5
                const targetAddr = addr.find((a: any) => Number(a?.status_id) !== 5);
                if (targetAddr) {
                    finalColor = targetAddr?.status_color || serviceColor || '';
                    finalBg = targetAddr?.bgColor || serviceBg || '';
                }
            } else if (serviceIsFinal) {
                const pendingAddr = addr.find((a: any) => !this.isFinalized(a?.status, a?.status_id));
                if (pendingAddr) {
                    finalColor = pendingAddr?.status_color || serviceColor || '';
                    finalBg = pendingAddr?.bgColor || serviceBg || '';
                }
            }
        }
        
        return { color: finalColor, bg: finalBg };
    }

    /**
     * Compute and set selectedAddressServiceId and nextAddressServiceId based on a row.
     * Per requirement: only when service status_id === 8 and there are address_services.
     */
    private computeAddressTargets(row: any): void {
        try {
            this.selectedAddressServiceId = null;
            this.nextAddressServiceId = null;
            const data = row || {};
            const addr = Array.isArray(data?.address_services) ? data.address_services : [];
            const serviceStatusId = Number(data?.status_id);
            if (addr.length > 0 && serviceStatusId === 5) {
                // First address whose status is NOT 5
                const idx = addr.findIndex((a: any) => Number(a?.status_id) !== 5);
                if (idx >= 0) {
                    this.selectedAddressServiceId = Number(addr[idx]?.id) || null;
                    const nextIdx = addr.findIndex((a: any, i: number) => i > idx && Number(a?.status_id) !== 5);
                    this.nextAddressServiceId = nextIdx >= 0 ? (Number(addr[nextIdx]?.id) || null) : null;
                }
            }
        } catch { }
    }

    address: any = []

    text_loading = "Cargando Información"
    data_combo: any = []
    dataCombo: any = []
    isVisible = true;

    evidence_1 = false
    evidence_2 = false

    files: File[] = [];
    url_page = environment.url_page
    public tooltipShowDelay = 0;
    public tooltipHideDelay = 10000;
    dataUser: any = {}

    public groupDisplayType: RowGroupingDisplayType = "groupRows";
    public groupRowRendererParams: any = {
        innerRenderer: GroupCellComponent
    }
    columnDefs: any = []

    public loadingCellRenderer: any = LoadingGridComponent;

    // (master-detail configuration removed)

    public loadingCellRendererParams: any = {
        loadingMessage: 'Espere un momento...',
    };
    public statusBar: {
        statusPanels: StatusPanelDef[];
    } = {
            statusPanels: [
                { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                { statusPanel: 'agTotalRowCountComponent', align: 'center' },
                { statusPanel: 'agFilteredRowCountComponent' },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agAggregationComponent' },
            ]
        };

    public defaultColDef = {
        sortable: true,
        flex: 1,
        filter: true,
        tooltipComponent: AddressTooltipComponent,
        resizable: true
    };

    // Community Plan B: manual detail rows
    private baseRows: any[] = [];
    private expandedIds = new Set<any>();

    // Shared tab
    private sharedGridApi!: GridApi;
    sharedGroups: any[] = [];
    sharedGroupsLoading = false;
    expandedGroups = new Set<number>();
    private sharedServiceIds = new Set<number>();

    public getRowHeight(params: any) {
        if (params?.data?.__isDetail) return 160;
        if (params?.data?.__isGroupHeader) return 52;
        return 48;
    }

    private gridApi!: GridApi
    service_id: any
    status_id: any
    // IDs for address-level status updates
    private selectedAddressServiceId: number | null = null;
    private nextAddressServiceId: number | null = null;

    public rowData: any = []
    public pageSize = 25
    public pageSizeOptions: number[] = [25, 50, 100, 200]
    public currentPage = 1
    public hasNextPage = false
    public conut: any = [{
        pending: 0,
        in_route: 0,
        good: 0
    }]

    text_type: any = ""
    evidences: any = []
    logs: any = []

    status: any = []

    dataClient: any = []

    formModel: any = {
        created_at: '',
        client: '',
        status: '',
        upload_date: '',
        download_date: '',
        folio: ''
    }

    private folioFilterTimeout: any = null;

    dataSelect: any = {}
    service_notes: any = ""

    listServices: any = []

    activeTab: 'unique' | 'shared' = 'unique'

    switchTab(tab: 'unique' | 'shared') {
        if (this.activeTab === tab) return;
        this.activeTab = tab;
        this.currentPage = 1;
        this.expandedIds.clear();
        this.expandedGroups.clear();
        if (tab === 'shared') {
            this.loadSharedList();
        } else {
            this.buildDatasource();
        }
    }

    private extractSharedIds(groups: any[]): void {
        this.sharedServiceIds.clear();
        groups.forEach(g => (g.services || []).forEach((s: any) => {
            const id = s.service?.id ?? s.id;
            if (id) this.sharedServiceIds.add(Number(id));
        }));
    }

    loadSharedList(): void {
        this.sharedGroupsLoading = true;
        this.expenseService.getSharedDepositsList(this.formModel).subscribe({
            next: (res: any) => {
                const groups: any[] = res?.data || [];
                this.extractSharedIds(groups);
                // Map each service inside the group from the deposits nested structure
                // to the flat field names used by the order-list column defs
                this.sharedGroups = groups.map(g => ({
                    ...g,
                    services: (g.services || []).map((s: any) => ({
                        ...s,
                        id:                  s.service?.id               ?? s.id,
                        folio:               s.service?.folio             ?? s.folio,
                        client:              s.service?.client?.name      ?? s.service?.client ?? s.client,
                        client_id:           s.service?.client_id,
                        unified_concat:      s.service?.unit?.unit        ?? s.service?.unit   ?? s.unified_concat,
                        unit_id:             s.service?.unit_id,
                        driver_id:           s.service?.driver_id,
                        assistant_id:        s.service?.assistant_id,
                        created_at:          s.service?.created_at        ?? s.created_at,
                        origin_address:      s.origin_address             ?? s.service?.origin_address,
                        destination_address: s.destination_address        ?? s.service?.destination_address,
                        date_start:          s.date_start,
                        date_end:            s.date_end,
                        charging_hour:       s.service?.charging_hour,
                        download_time:       s.service?.download_time,
                        upload_date:         s.service?.upload_date,
                        download_date:       s.service?.download_date,
                        departure_date:      s.service?.departure_date,
                        departure_time:      s.service?.departure_time,
                        observation:         s.service?.observation,
                        requires_trailer:    s.service?.requires_trailer  ?? false,
                        trailer_id:          s.service?.trailer_id,
                        bands:               s.service?.bands,
                        roller_skates:       s.service?.roller_skates,
                        beach:               s.service?.beach,
                        devils:              s.service?.devils,
                        mats:                s.service?.mats,
                        cartons:             s.service?.cartons,
                        status_id:           s.status_id,
                        status_id_one:       s.status_id_one              ?? 0,
                        status_id_two:       s.status_id_two              ?? 0,
                        bgColor:             s.bgColor,
                        status_color:        s.status_color,
                        address_services:    s.address_services           ?? s.service?.address_services,
                        status:              s.status,
                    }))
                }));
                this.sharedGroupsLoading = false;
                if (this.sharedGridApi) {
                    this.sharedGridApi.setGridOption('rowData', this.buildSharedDisplayRows());
                    this.sharedGridApi.resetRowHeights();
                }
            },
            error: () => { this.sharedGroupsLoading = false; }
        });
    }

    onSharedGridReady(params: any): void {
        if (params?.api) this.sharedGridApi = params.api as GridApi;
        try {
            this.sharedGridApi.setGridOption('getRowHeight', this.getRowHeight as any);
            this.sharedGridApi.setGridOption('suppressScrollOnNewData', true as any);
            this.sharedGridApi.setGridOption('pagination', false);
            this.sharedGridApi.setGridOption('suppressPaginationPanel', true as any);
            // Grid only renders when sharedGroups.length > 0, so data is always available here
            this.sharedGridApi.setGridOption('rowData', this.buildSharedDisplayRows());
            this.sharedGridApi.resetRowHeights();
        } catch { }
    }

    onSharedCellClicked(event: any): void {
        const row = event?.data;
        if (row?.__isDetail) return;
        if (row?.__isGroupHeader) {
            const clickTarget = (event.event?.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
            if (clickTarget) {
                const action = clickTarget.getAttribute('data-action');
                if (action === 'registrar-viaticos') {
                    const sid = row.first_active_service_id;
                    if (sid) this.router.navigate(['/service/expense-report', sid], { queryParams: { from: 'services', tab: 'shared' } });
                }
                return;
            }
            const gid: number = row.group_id;
            if (this.expandedGroups.has(gid)) this.expandedGroups.delete(gid); else this.expandedGroups.add(gid);
            if (this.sharedGridApi) {
                this.sharedGridApi.setGridOption('rowData', this.buildSharedDisplayRows());
                this.sharedGridApi.resetRowHeights();
                try { setTimeout(() => this.sharedGridApi.onRowHeightChanged(), 120); } catch { }
            }
            return;
        }
        if (event.colDef.field === 'folio') {
            const sid = row?.id;
            if (this.expandedIds.has(sid)) this.expandedIds.delete(sid); else this.expandedIds.add(sid);
            if (this.sharedGridApi) {
                this.sharedGridApi.setGridOption('rowData', this.buildSharedDisplayRows());
                this.sharedGridApi.resetRowHeights();
                try { setTimeout(() => this.sharedGridApi.onRowHeightChanged(), 120); } catch { }
            }
            return;
        }
        if (event.colDef.field !== 'status_id' && event.colDef.field !== 'id') {
            this.computeAddressTargets(row);
            this.router.navigateByUrl('/service/detail', { state: { data: row, status: this.data_combo, next_address_service_id: this.nextAddressServiceId, address_service_id: this.selectedAddressServiceId } });
        }
    }

    ngOnInit(): void {
        const tabParam = this.route.snapshot.queryParamMap.get('tab');
        if (tabParam === 'shared' || tabParam === 'unique') {
            this.activeTab = tabParam;
            if (tabParam === 'shared') {
                this.loadSharedList();
            }
        }

        this.expenseService.getSharedDepositsList({}).subscribe({
            next: (res: any) => this.extractSharedIds(res?.data || []),
            error: () => {}
        });

        const servicio1$ = this.apiClient.getInfo();
        const servicio2$ = this.apiService.status();
        forkJoin([servicio1$, servicio2$]).subscribe(([resp1, resp2]) => {
            this.data_combo = Array.isArray(resp2) ? resp2 : ((resp2 as any)?.data || []);
            this.dataClient = resp1
            this.spinner.hide()
        })

        this.dataUser = this.utils.getDataUser()
        if (this.dataUser.type_admin == 5) {
            this.columnDefs = [
                { field: "origin_address", headerName: "Origen", width: 200, cellClass: "textEllipsis", tooltipField: "origin_address", tooltipComponentParams: { color: "#ececec" } },
                { field: 'destination_address', headerName: "Destino", width: 200, cellClass: 'textEllipsis', tooltipField: 'destination_address', tooltipComponentParams: { color: '#ececec' },
                    cellRenderer: (params: ICellRendererParams) => {
                        const address = params?.value;
                        if (address && address.trim()) {
                            return address;
                        } else {
                            return '<span style="background: #F9E3E3; color: #A62E2E; padding: 2px 6px; border-radius: 3px; font-size: 11px;">Destino pendiente</span>';
                        }
                    }
                },
                {
                    field: 'status_id', headerName: "Estado",
                    cellRenderer: StatusSelectComponent,
                    cellRendererParams: { parent: this, data_combo: this.data_combo, dataUser: this.dataUser }
                }
            ];
        } else {
            const folioCol: ColDef = {
                field: 'folio',
                headerName: 'Folio',
                cellRendererSelector: (p: ICellRendererParams) => {
                    if (p.data?.__isDetail) {
                        return { component: DetailPanelComponent, params: { parent: this } } as any;
                    }
                    if (p.data?.__isGroupHeader) {
                        return {
                            component: (params: ICellRendererParams) => {
                                const g = params.data;
                                const expanded = this.expandedGroups.has(g.group_id);
                                const chevron = expanded ? 'ti-chevron-up' : 'ti-chevron-down';
                                const count = g.services_count ?? 0;
                                const activeCount = g.active_services_count ?? 0;
                                const hasExtra = g.unit_name || g.operator_name;
                                const divider = hasExtra ? `<div style="width:1px;height:34px;background:#C8DFF0;margin:0 12px;flex-shrink:0;"></div>` : '';
                                const unitBlock = g.unit_name ? `<div style="display:flex;flex-direction:column;gap:1px;margin-right:20px;"><span style="font-size:12px;color:#707070;line-height:1.5;">Unidad</span><span style="font-size:12px;color:#545454;font-weight:500;line-height:1.3;">${g.unit_name}</span></div>` : '';
                                const opBlock = g.operator_name ? `<div style="display:flex;flex-direction:column;gap:1px;"><span style="font-size:12px;color:#707070;line-height:1.5;">Operador</span><span style="font-size:12px;color:#545454;font-weight:500;line-height:1.3;">${g.operator_name}</span></div>` : '';
                                const registerBtn = activeCount >= 2
                                    ? `<button data-action="registrar-viaticos" style="background:#fff;border:1.5px solid #00A0E1;color:#00A0E1;border-radius:8px;padding:4px 14px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;line-height:1.5;margin-right:10px;">Registrar viáticos</button>`
                                    : '';
                                return `
                                  <div style="display:flex;align-items:center;justify-content:space-between;width:100%;height:100%;padding:0 16px;cursor:pointer;box-sizing:border-box;">
                                    <div style="display:flex;align-items:center;">
                                      <span style="background:#00A0E1;color:#fff;font-size:12px;font-weight:700;padding:0 12px;border-radius:6px;height:26px;line-height:26px;white-space:nowrap;flex-shrink:0;">${g.group_code}</span>
                                      <span style="color:#555;font-size:13px;margin-left:12px;white-space:nowrap;">${count} Servicio${count !== 1 ? 's' : ''} compartiendo viáticos</span>
                                      ${divider}${unitBlock}${opBlock}
                                    </div>
                                    <div style="display:flex;align-items:center;flex-shrink:0;">
                                      ${registerBtn}
                                      <span class="ti ${chevron}" style="color:#888;font-size:18px;margin-left:4px;"></span>
                                    </div>
                                  </div>`;
                            }
                        } as any;
                    }
                    return {
                        component: (params: ICellRendererParams) => {
                            const id = params?.data?.id;
                            const expanded = this.expandedIds.has(id);
                            const icon = expanded ? 'ti-chevron-down' : 'ti-chevron-right';
                            const folio = params?.value ?? '';
                            const createdAt = params?.data?.created_at;
                            return `
                <div style="display:flex;align-items:flex-start;gap:6px;line-height:1.2">
                  <span class="ti ${icon}" style="margin-top:2px"></span>
                  <div>
                    <div>${folio}</div>
                    ${createdAt ? `<div style="font-size:11px;color:#6c757d;">${createdAt}</div>` : ''}
                  </div>
                </div>
              `;
                        }
                    } as any;
                },
                colSpan: (p: any) => {
                    if (p?.data?.__isDetail || p?.data?.__isGroupHeader) {
                        const left = p.api.getDisplayedLeftColumns ? p.api.getDisplayedLeftColumns().length : 0;
                        const center = p.api.getDisplayedCenterColumns ? p.api.getDisplayedCenterColumns().length : 0;
                        const right = p.api.getDisplayedRightColumns ? p.api.getDisplayedRightColumns().length : 0;
                        return left + center + right;
                    }
                    return 1;
                },
                cellStyle: (p: any) => {
                    if (p?.data?.__isDetail) return { padding: 0 };
                    if (p?.data?.__isGroupHeader) return { padding: 0, background: '#EBF5FF', borderBottom: '1px solid #C8E1F5' };
                    return { display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'left' } as any;
                },
            };
            this.columnDefs = [
                folioCol,
                { field: 'client', headerName: "Cliente", cellClass: 'textEllipsis' },
                { field: 'unified_concat', headerName: "Unidad", cellRenderer: GroupCellComponent, cellClass: 'textEllipsis'},
                { field: 'date_start', headerName: "Fecha y hora de carga", width: 120,cellClass: 'textEllipsis' },
                { field: 'origin_address', headerName: "Origen", width: 200, cellClass: 'textEllipsis', tooltipField: 'origin_address', tooltipComponentParams: { color: '#ececec' } },
                { field: 'date_end', headerName: "Fecha y hora de descarga", width: 120, cellClass: 'textEllipsis',
                    cellRenderer: (params: ICellRendererParams) => {
                        const date = params?.value;
                        if (date && date.trim()) {
                            return date;
                        } else {
                            return 'Prog. pendiente';
                        }
                    },
                    cellStyle: (params: any) => {
                        if (!params?.value || !params.value.trim()) {
                            return { backgroundColor: '#F9E3E3', color: '#A62E2E', border: '1px solid #fff !important', borderRadius: '3px', fontWeight: 'bold' } as any;
                        }
                        return {} as any;
                    },
                },
                { field: 'destination_address', headerName: "Destino", width: 200, cellClass: 'textEllipsis', tooltipField: 'destination_address', tooltipComponentParams: { color: '#ececec' },
                    cellRenderer: (params: ICellRendererParams) => {
                        const address = params?.value;
                        if (address && address.trim()) {
                            return address;
                        } else {
                            return 'Destino pendiente';
                        }
                    },
                    cellStyle: (params: any) => {
                        if (!params?.value || !params.value.trim()) {
                            return { backgroundColor: '#F9E3E3', color: '#A62E2E', border: '1px solid #fff !important', borderRadius: '3px', fontWeight: 'bold' } as any;
                        }
                        return {} as any;
                    },
                    tooltipValueGetter: (params: any) => {
                        const address = params?.value;
                        return (address && address.trim()) ? address : 'Destino pendiente';
                    }
                },
                {
                    field: 'status_id', headerName: "Estado",
                    minWidth: 180,
                    filter: false,
                    sortable: false,
                    suppressSizeToFit: true,
                    cellStyle: (params: any) => {
                        const statusColors = this.getStatusColor(params?.data);
                        return { 
                            backgroundColor: statusColors.bg,
                            color: statusColors.color 
                        } as any;
                    },
                    cellRenderer: StatusSelectComponent,
                    cellRendererParams: { parent: this, data_combo: this.data_combo, dataUser: this.dataUser }
                },
                {
                    field: 'id', headerName: "Acciones",
                    width: 100,
                    filter: false,
                    sortable: false,
                    cellRenderer: BtnActionComponent,
                    cellRendererParams: { parent: this, dataUser: this.dataUser }
                }
            ];
        }

        // Datepickers are initialized after the view is rendered
    }

    ngAfterViewInit(): void {
        // Delay to ensure the template is painted and *ngIf sections are in the DOM
        setTimeout(() => this.initDatepickers(), 0);
    }

    // Some sections of the template are behind *ngIf (permissions). On hard refresh, the component lifecycle
    // may complete before those sections render. Use AfterViewChecked to attempt a one-time lazy init
    // once the inputs actually exist in the DOM.
    private datepickersInitialized = false;
    ngAfterViewChecked(): void {
        if (this.datepickersInitialized) return;
        try {
            const uploadExists = typeof $ !== 'undefined' && $("#upload_date").length > 0;
            const downloadExists = typeof $ !== 'undefined' && $("#download_date").length > 0;
            if (uploadExists || downloadExists) {
                this.initDatepickers();
                this.datepickersInitialized = true;
            }
        } catch { }
    }

    private initDatepickers(): void {
        try {
            if (!$.fn || !$.fn.datepicker) {
                console.warn('jQuery UI datepicker is not loaded. Ensure jQuery UI JS and CSS are included.');
                return;
            }

            // Upload date picker
            const $upload = $("#upload_date");
            if ($upload.length) {
                $upload.datepicker({
                    dateFormat: 'yy-mm-dd',
                    onSelect: () => {
                        $upload.trigger('change');
                    },
                }).change(() => {
                    this.formModel.upload_date = $upload.datepicker({ dateFormat: 'yy-mm-dd' }).val();
                    this.filter();
                });
            }

            // Download date picker
            const $download = $("#download_date");
            if ($download.length) {
                $download.datepicker({
                    dateFormat: 'yy-mm-dd',
                    onSelect: () => {
                        $download.trigger('change');
                    }
                }).change(() => {
                    this.formModel.download_date = $download.datepicker({ dateFormat: 'yy-mm-dd' }).val();
                    this.filter();
                });
            }
            // Mark as initialized to avoid repeated setup
            this.datepickersInitialized = true;
        } catch (e) {
            console.error('Failed to initialize datepickers', e);
        }
    }

    removeDate(id: string) {
        const $el = $("#" + id);
        if (!$el.length || !$.fn || !$.fn.datepicker) {
            // Fallback: just clear the model and trigger filter
            if (id === 'upload_date') {
                this.formModel.upload_date = '';
            } else if (id === 'download_date') {
                this.formModel.download_date = '';
            }
            this.filter();
            return;
        }
        $el.datepicker('setDate', null).trigger("change");
    }

    eventIsVisible() {
        this.isVisible = !this.isVisible
        if (this.isVisible) {

        } else {
            this.formModel.created_at = ''
            this.formModel.client = ''
            this.formModel.status = ''
            this.formModel.upload_date = ''
            this.formModel.download_dat = ''
            this.formModel.folio = ''
            this.refreshGrid()
        }
    }

    event_text = ''

    filter() {
        // Refetch data with current filters
        // Reset to first page for external pager
        this.currentPage = 1;
        this.buildDatasource();
        // Optionally refresh counters using existing endpoint
        this.spinner.show()
        this.apiService.filter(this.formModel).subscribe((resp: any) => {
            if (resp && resp.count) {
                this.conut = resp.count
            }
            this.spinner.hide()
        }, () =>    
            this.spinner.hide()
        )
    }

    onSelectClient() {
        this.filter()
    }
    onSelectStatus() {
        this.filter()
    }
    onFolioInput() {
        clearTimeout(this.folioFilterTimeout);
        this.folioFilterTimeout = setTimeout(() => this.filter(), 400);
    }

    onGridReady(params: any) {
        this.spinner.show()
        if (params && params.api) {
            this.gridApi = params.api as GridApi
        }
        // Configure grid options via API
        try {
            if (this.gridApi) {
                this.gridApi.setGridOption('getRowHeight', this.getRowHeight as any);
                this.gridApi.setGridOption('tooltipShowDelay', this.tooltipShowDelay as any);
                this.gridApi.setGridOption('tooltipHideDelay', this.tooltipHideDelay as any);
                this.gridApi.setGridOption('suppressScrollOnNewData', true as any);
                this.gridApi.setGridOption('pagination', false);
                this.gridApi.setGridOption('suppressPaginationPanel', true as any);
                // Re-anchor __isDetail rows to their master after any AG Grid sort
                this.gridApi.setGridOption('postSortRows', (params: any) => {
                    const nodes: any[] = params.nodes;
                    const detailByMasterId = new Map<any, any>();
                    const masterNodes: any[] = [];
                    for (const node of nodes) {
                        if (node.data?.__isDetail) {
                            detailByMasterId.set(node.data.master?.id, node);
                        } else {
                            masterNodes.push(node);
                        }
                    }
                    nodes.length = 0;
                    for (const master of masterNodes) {
                        nodes.push(master);
                        const detail = detailByMasterId.get(master.data?.id);
                        if (detail) nodes.push(detail);
                    }
                });
            }
        } catch {
        }

        this.buildDatasource();
    }

    private buildDatasource(): void {
        // Use external pager state (Option A)
        const currentPage = this.currentPage > 0 ? this.currentPage : 1;
        const payload: any = {
            ...this.formModel,
            page: currentPage,
            pageSize: this.pageSize,
            sortModel: [],
            filterModel: {}
        };
        this.spinner.show();
        this.apiService.filter(payload).subscribe(
            (resp: any) => {
                // Update counters if provided
                if (resp && resp.count) {
                    this.conut = resp.count;
                }
                this.baseRows = (Array.isArray(resp?.data) ? resp.data : [])
                    .filter((r: any) => !this.sharedServiceIds.has(Number(r.id)));
                // Infer if there is next page when backend does not return total
                this.hasNextPage = Array.isArray(resp?.data) && resp.data.length === this.pageSize;
                const display = this.buildDisplayRows();
                if (this.gridApi) {
                    this.gridApi.setRowData(display);
                    this.gridApi.resetRowHeights();
                    // Allow any existing detail rows to auto-size once their content renders
                    try { setTimeout(() => this.gridApi.onRowHeightChanged(), 0); } catch { }
                    try { setTimeout(() => this.gridApi.onRowHeightChanged(), 120); } catch { }
                }
                this.spinner.hide();
            },
            () => {
                this.spinner.hide();
            }
        );
    }

    onPageSizeChanged(newSize: number | string) {
        const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
        if (!isNaN(size as number)) {
            this.pageSize = size as number;
            // Reset to first page and rebuild datasource to send updated page/pageSize
            this.currentPage = 1;
            this.buildDatasource();
        }
    }

    nextPage() {
        if (this.hasNextPage) {
            this.currentPage += 1;
            this.buildDatasource();
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
            this.buildDatasource();
        }
    }
    actionDelete(id: number) {
        alert("click")
    }
    changedStatus() {
        let status_id = $('input[name="status_option"]:checked').val()

        if (!status_id) {
            this.utils.alertSwal({ text: "Seleccione el estado que desea actualizar.", title: "Advertencia.", icon: "warning" });
            return
        }

        $("#modalStatus").modal("hide")
        this.utils.alertSwalConfir({ text: "Esta seguro que desea actualizar el estado el servicio", title: "Actualización de estado", icon: "warning", btn_confirm: "Aceptar" }).then((resp) => {
            if (resp.isConfirmed) {
                this.spinner.show()
                // Default to service-level update; backend expects address service ids even if not used
                const address_service_id = this.selectedAddressServiceId || null;
                const next_address_service_id = this.nextAddressServiceId || null;
                this.apiService.updateStatus(this.service_id, status_id, this.dataUser.id, address_service_id, next_address_service_id).subscribe({
                    next: (response) => {
                        this.utils.alertSwal({ text: "Se ha realizado el cambio de estado de forma exitosa.", title: "Actualización.", icon: "success" });
                        this.spinner.hide()
                        this.refreshGrid()
                    },
                    error: (err) => {
                        this.spinner.hide()
                        const text = err?.error?.msg || "No se pudo actualizar el estado del servicio.";
                        this.utils.alertSwal({ text, title: "No se pudo actualizar", icon: "error" });
                    }
                })
            } else {
                this.spinner.hide()
                this.refreshGrid()
            }
        })
    }

    modalActiveEvidence(id: any, status: any) {
        $("#modalEvidenceOption").modal("hide")
        this.status_id = status
        this.service_id = id
        $("#modalEvidence").modal("show")
    }

    onSelect(event: any) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event: any) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    uploadEvidences() {
        // $("#modalEvidence").modal("hide")
        // this.spinner.show()
        // const formData = new FormData();
        // for (var i = 0; i < this.files.length; i++) {
        //     formData.append("file[]", this.files[i]);
        // }
        // const nextStatus = this.getNextStatusId(this.status_id);
        // this.apiService.uploadFile(formData, this.service_id, this.status_id, this.dataUser.id, nextStatus).subscribe((resp) => {
        //     this.spinner.hide()
        //     this.files = []
        //     this.utils.alertSwal({ title: "Evidencias", text: "Se han cargado las evidencias de forma exitosa", icon: "success" })
        //     this.refreshGrid()
        // }, (error) => {
        //     this.spinner.hide()
        //     this.utils.alertSwal({ title: "Evidencias", text: "Se han presentado un error en el cargue de las evidencias", icon: "error" })
        // })
    }
    dowloandService(id: any) {
        let url = environment.url_page
        window.open(url + '/generate-pdf/' + id, "_blank")
    }

    modalOpenEvidence(id: number, status_id: any) {
        $("#modalEvidenceOption").modal("hide")
        this.text_type = status_id == 3 ? "Carga" : "Descarga"
        this.spinner.show()
        this.apiService.getEvidences({ orden_id: id, status_id: status_id }).subscribe((resp) => {
            this.spinner.hide()
            this.evidences = resp
            $("#modalEvidenceShow").modal("show")
        }, (error) => {
            this.spinner.hide()
        })
    }
    cancelOrder(id: number) {

        this.utils.alertSwalConfir({ text: "Esta seguro que desea cancelar el servicio", title: "Cancelación del servicio", icon: "warning", btn_confirm: "Aceptar" }).then((resp) => {
            if (resp.isConfirmed) {
                this.spinner.show()
                this.apiService.cancelOrder(id, this.dataUser.id).subscribe((response) => {
                    this.utils.alertSwal({ text: "Se ha realizado la cancelación del servicio de forma exitosa.", title: "Cancelacion del servicio.", icon: "success" });
                    this.spinner.hide()
                    this.refreshGrid()
                })
            } else {
                this.spinner.hide()
                this.refreshGrid()
            }
        })
    }

    logsService(data: any) {

        this.logs = data
        $("#modalLogs").modal("show")
    }

    updateObservation(data: any) {
        this.dataSelect = data
        $("#modalObservation").modal("show")
    }

    updateObservationSend() {
        $("#modalObservation").modal("hide")
        this.spinner.show()

        this.apiService.observation({ observation: this.dataSelect.observation }, this.dataSelect.id).subscribe((resp) => {
            this.utils.alertSwal({ text: "Se actualizado la observacion de forma exitosa.", title: "Observación del servicio.", icon: "success" });
            this.spinner.hide()
            this.refreshGrid()
        }, (error) => {
            this.spinner.hide();
            this.utils.alertSwal({ title: "Observación ", text: "Se han presentado un error al actualizar la observacion", icon: "error" })
        })
    }
    clickStatusChanged(idOrder: number, combo_data: any, rowData?: any) {
        this.dataCombo = combo_data
        this.service_id = idOrder
        this.dataSelect = rowData
        // Pre-compute address service ids if applicable (status_id === 8)
        this.computeAddressTargets(rowData || {});
        $("#modalStatus").modal("show")
        $('input[name="status_option"]').prop("checked", false)
    }
    openModalChangedUnit(id: number, units: any) {
        this.service_id = id
        $("#modalChangedUnit").modal("show")
    }
    updateOrderUnit() {

    }
    openModalEvidenceOption(id: number, status_id: number, evidence_1: boolean, evidence_2: boolean) {
        this.service_id = id
        this.status_id = status_id
        this.evidence_1 = evidence_1
        this.evidence_2 = evidence_2
        $("#modalEvidenceOption").modal("show")
    }
    /**
     * Resolve next status id from status combo if provided, else fallback by simple rule.
     */
    private getNextStatusId(current: any): any {
        try {
            const st: any = Array.isArray(this.data_combo) ? this.data_combo.find((s: any) => s?.id == current) : null;
            const explicit = st?.next_status ?? st?.next_status_id ?? st?.nextId;
            if (explicit != null && explicit !== undefined) return explicit;
        } catch { }
        switch (current) {
            case 3:
                return 8; // Adjust if your workflow uses a different "descarga" status
            default:
                return current;
        }
    }
    onCellClicked(event: any) {
        if (event.colDef.field === 'folio') {
            const row = event?.data;
            if (!row?.__isDetail) {
                // Remember the current index so we can keep it in view after re-render
                const targetIndex = event?.rowIndex ?? 0;
                const id = row?.id;
                if (this.expandedIds.has(id)) this.expandedIds.delete(id); else this.expandedIds.add(id);
                const display = this.buildDisplayRows();
                if (this.gridApi) {
                    this.gridApi.setRowData(display);
                    this.gridApi.resetRowHeights();
                    // Ensure the previously clicked row stays visible (avoid jumping to top)
                    try { this.gridApi.ensureIndexVisible(targetIndex, 'middle'); } catch { }
                    // Give detail renderer time to mount and report its measured height
                    try { setTimeout(() => this.gridApi.onRowHeightChanged(), 0); } catch { }
                    try { setTimeout(() => this.gridApi.onRowHeightChanged(), 120); } catch { }
                }
            }
            return;
        }
        // Default navigation behavior for other cells
        if (event.colDef.field != 'status_id' && event.colDef.field != 'id' && !event.data?.__isGroupHeader && !event.data?.__isDetail) {
            const row = event?.data || {};
            this.computeAddressTargets(row);
            // Align keys with OrderDetail expectations (address_service_id, next_address_service_id)
            this.router.navigateByUrl('/service/detail', { state: { data: event.data, status: this.data_combo, next_address_service_id: this.nextAddressServiceId, address_service_id: this.selectedAddressServiceId } });
        }
    }

    addNota(data: any) {
        this.router.navigateByUrl('/service/changed-unit', { state: data });
    }

    addNoteApp() {
        $("#modalObservation").modal("hide")
        this.spinner.show()

        this.apiServiceNote.addNote({ note: this.service_notes, user_id: this.dataUser.id, service_id: this.dataSelect.id }).subscribe((resp) => {
            this.utils.alertSwal({ text: "Se agrego la nota de forma exitosa.", title: "Nota del servicio.", icon: "success" });
            this.service_notes = ""
            this.spinner.hide()
            this.refreshGrid()
        }, (error) => {
            this.spinner.hide();
            this.utils.alertSwal({ title: "Nota ", text: "Se han presentado un error al agregar la nota", icon: "error" })
        })
    }
    // Permission helper for templates
    canDo(moduleKey: string, actionName: string): boolean {
        return this.permissionService.canDo(moduleKey, actionName);
    }

    routerLink(data:any) {
        $("#modalStatus").modal("hide")
        this.router.navigateByUrl('/maintenance/coordinator/add', { state: { data: data } });
    }
}
